<template>
  <div class="max-w-full mx-auto my-5 px-4 sm:px-6">
    <div class="rounded-2xl border bg-white dark:bg-gray-700 dark:border-gray-600 shadow-sm overflow-hidden">
      <div
        class="px-6 py-5 border-b dark:border-gray-600 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">Ticket Schedules</h1>
        </div>

        <div class="flex items-center gap-3">
          <button type="button" @click="fetchSchedules"
            class="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-500 dark:hover:bg-gray-600">
            Refresh
          </button>
          <button type="button" @click="$router.push({ name: 'newTicketSchedule' })"
            class="px-4 py-2 rounded-md text-sm font-medium text-white bg-yellow-500 hover:bg-yellow-600">
            Create Schedule
          </button>
        </div>
      </div>

      <div class="px-6 py-4 border-b dark:border-gray-600 bg-gray-50 dark:bg-gray-800/40">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <div class="xl:col-span-2">
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-1">
              Search
            </label>
            <input v-model="filters.search" @keyup.enter="applyFilters" type="text"
              placeholder="Description, requester, email, tech..."
              class="w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-700 dark:text-gray-100" />
          </div>

          <div>
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-1">
              Status
            </label>
            <select v-model="filters.enabled"
              class="w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-700 dark:text-gray-100">
              <option value="">All</option>
              <option value="1">Enabled</option>
              <option value="0">Disabled</option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-1">
              Schedule Type
            </label>
            <select v-model="filters.schedule_type"
              class="w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-700 dark:text-gray-100">
              <option value="">All</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div class="flex items-end gap-2">
            <button type="button" @click="applyFilters"
              class="w-full px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
              Apply
            </button>
            <button type="button" @click="resetFilters"
              class="w-full px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500 dark:hover:bg-gray-600">
              Clear
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="py-16 text-center">
        <div class="inline-flex items-center gap-3 text-gray-600 dark:text-gray-200">
          <svg class="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" class="opacity-25" />
            <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" stroke-width="3" class="opacity-75" />
          </svg>
          Loading schedules...
        </div>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead class="bg-gray-50 dark:bg-gray-800/70">
            <tr>
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                <button type="button" class="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white"
                  @click="onSort('description')">
                  Description <span class="text-xs">{{ sortIcon('description') }}</span>
                </button></th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                <button type="button" class="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white"
                  @click="onSort('client')">
                  Client <span class="text-xs">{{ sortIcon('client') }}</span>
                </button></th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                <button type="button" class="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white"
                  @click="onSort('tech')">
                  Tech <span class="text-xs">{{ sortIcon('tech') }}</span>
                </button></th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                <button type="button" class="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white"
                  @click="onSort('schedule_type')">
                  Type <span class="text-xs">{{ sortIcon('schedule_type') }}</span>
                </button></th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                <button type="button" class="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white"
                  @click="onSort('promised_time')">
                  Promised Time <span class="text-xs">{{ sortIcon('promised_time') }}</span>
                </button></th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                <button type="button" class="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white"
                  @click="onSort('next_run_date')">
                  Next Run <span class="text-xs">{{ sortIcon('next_run_date') }}</span>
                </button></th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                <button type="button" class="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white"
                  @click="onSort('lead_time_days')">
                  Lead Time <span class="text-xs">{{ sortIcon('lead_time_days') }}</span>
                </button></th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                <button type="button" class="inline-flex items-center gap-1 uppercase hover:text-gray-900 dark:hover:text-white"
                  @click="onSort('is_enabled')">
                  Enabled <span class="text-xs">{{ sortIcon('is_enabled') }}</span>
                </button></th>
              <th
                class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-200">
                Actions</th>
            </tr>
          </thead>

          <tbody class="divide-y divide-gray-200 dark:divide-gray-600">
            <tr v-for="schedule in schedules" :key="schedule.id"
              class="bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-650 transition-colors">
              <td class="px-4 py-4 align-top">
                <div class="font-medium text-gray-900 dark:text-gray-100">
                  {{ schedule.description || '—' }}
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-300 mt-1">
                  {{ schedule.requester || 'No requester' }}
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-300">
                  {{ schedule.email || '—' }}
                </div>
              </td>

              <td class="px-4 py-4 align-top text-sm text-gray-700 dark:text-gray-100">
                <div>{{ schedule.client?.shortname || schedule.client?.companyname || schedule.company_id || '—' }}
                </div>
              </td>

              <td class="px-4 py-4 align-top text-sm text-gray-700 dark:text-gray-100">
                {{ schedule.tech_id
                  ? (schedule.user_profile?.firstname || '-') + ' ' + (schedule.user_profile?.lastname || '-')
                  : 'Delegator'
                }}
              </td>

              <td class="px-4 py-4 align-top text-sm text-gray-700 dark:text-gray-100">
                <div class="font-medium">{{ prettyScheduleType(schedule) }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-300 mt-1">{{ recurrenceSummary(schedule) }}</div>
              </td>

              <td class="px-4 py-4 align-top text-sm text-gray-700 dark:text-gray-100">
                {{ formatTime(schedule.promised_time) }}
              </td>

              <td class="px-4 py-4 align-top text-sm text-gray-700 dark:text-gray-100">
                {{ formatDate(schedule.next_run_date) }}
              </td>

              <td class="px-4 py-4 align-top text-sm text-gray-700 dark:text-gray-100">
                {{ schedule.lead_time_days ?? 0 }} day<span v-if="Number(schedule.lead_time_days) !== 1">s</span>
              </td>

              <td class="px-4 py-4 align-top">
                <button type="button" @click="toggleSchedule(schedule)"
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                  :class="Number(schedule.is_enabled) === 1 ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500'">
                  <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                    :class="Number(schedule.is_enabled) === 1 ? 'translate-x-6' : 'translate-x-1'" />
                </button>
              </td>

              <td class="px-4 py-4 align-top text-right">
                <button type="button" @click="$router.push({ name: 'editTicketSchedule', params: { id: schedule.id } })"
                  class="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                  Edit
                </button>
              </td>
            </tr>

            <tr v-if="!schedules.length">
              <td colspan="10" class="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-300">
                No schedules found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="pagination.total > 0"
        class="px-6 py-4 border-t dark:border-gray-600 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div class="text-sm text-gray-600 dark:text-gray-200">
          Showing {{ pagination.from }} - {{ pagination.to }} of {{ pagination.total }}
        </div>

        <div class="flex items-center gap-2">
          <button type="button" @click="changePage(pagination.current_page - 1)"
            :disabled="pagination.current_page <= 1"
            class="px-3 py-2 rounded-md border border-gray-300 text-sm bg-white text-gray-700 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500">
            Previous
          </button>
          <div class="px-3 py-2 text-sm text-gray-700 dark:text-gray-100">
            Page {{ pagination.current_page }} / {{ pagination.last_page }}
          </div>
          <button type="button" @click="changePage(pagination.current_page + 1)"
            :disabled="pagination.current_page >= pagination.last_page"
            class="px-3 py-2 rounded-md border border-gray-300 text-sm bg-white text-gray-700 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'TicketSchedules',
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      loading: false,
      schedules: [],
      filters: {
        search: '',
        enabled: '',
        schedule_type: '',
        company_id: ''
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
        total: 0,
        per_page: 25
      },
      // Empty `by` = backend default (newest first).
      sort: {
        by: '',
        dir: 'asc'
      },
      userroles: [],
      userPermissions: []
    }
  },
  async mounted() {
    const allowed = await this.bootstrapAccess()
    if (!allowed) return
    await this.fetchSchedules()
  },
  methods: {
    async bootstrapAccess() {
      try {
        const userId = localStorage.getItem('user_id')
        const response = await axios.get(`user/role/${userId}`)
        this.userroles = response.data.roles || []
        this.userPermissions = response.data.user_permissions || []

        if (!this.hasSchedulerAccess()) {
          this.toast.error('You do not have access to Ticket Schedules.')
          this.$router.replace({ name: 'viewTickets' })
          return false
        }

        return true
      } catch (error) {
        console.error('Error loading user access:', error)
        this.toast.error('Failed to verify scheduler access.')
        this.$router.replace({ name: 'viewTickets' })
        return false
      }
    },
    hasUserPermission(module, name) {
      return (this.userPermissions || []).some(p =>
        p.module === module &&
        p.name === name &&
        String(p.value) !== '0'
      )
    },
    hasSchedulerAccess() {
      const roles = this.userroles || []
      // Ticket scheduler is internal-only — external roles (60/70/80/100) never get in
      // regardless of any explicit ticket_scheduler.access permission.
      const isExternal = roles.some(r => [60, 70, 80, 100].includes(Number(r)))
      if (isExternal) return false
      const byRole = roles.includes(10) || roles.includes(20)
      return byRole || this.hasUserPermission('ticket_scheduler', 'access')
    },
    async fetchSchedules(page = 1) {
      this.loading = true
      try {
        const params = {
          page,
          per_page: this.pagination.per_page
        }

        if (this.filters.search) params.search = this.filters.search
        if (this.filters.enabled !== '') params.enabled = this.filters.enabled
        if (this.filters.schedule_type) params.schedule_type = this.filters.schedule_type
        if (this.filters.company_id) params.company_id = this.filters.company_id
        if (this.sort.by) {
          params.sort_by = this.sort.by
          params.sort_dir = this.sort.dir
        }

        const response = await axios.get('ticket-schedules', { params })
        const data = response.data || {}

        this.schedules = data.data || []
        this.pagination.current_page = data.current_page || 1
        this.pagination.last_page = data.last_page || 1
        this.pagination.from = data.from || 0
        this.pagination.to = data.to || 0
        this.pagination.total = data.total || 0
        this.pagination.per_page = data.per_page || 25
      } catch (error) {
        console.error('Error loading schedules:', error)
        this.toast.error('Failed to load ticket schedules.')
      } finally {
        this.loading = false
      }
    },
    applyFilters() {
      this.fetchSchedules(1)
    },
    resetFilters() {
      this.filters = {
        search: '',
        enabled: '',
        schedule_type: '',
        company_id: ''
      }
      this.fetchSchedules(1)
    },
    sortIcon(col) {
      if (this.sort.by !== col) return '↕'
      return this.sort.dir === 'asc' ? '↑' : '↓'
    },
    onSort(col) {
      if (this.sort.by === col) {
        this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.by = col
        this.sort.dir = 'asc'
      }
      this.fetchSchedules(1)
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.last_page) return
      this.fetchSchedules(page)
    },
    async toggleSchedule(schedule) {
      // Optimistic — flip the UI immediately so the click feels responsive.
      // If the API errors, roll back and toast. On success, do nothing extra
      // (the most-recent click is already reflected on screen).
      const original = Number(schedule.is_enabled)
      schedule.is_enabled = original === 1 ? 0 : 1

      try {
        await axios.post(`ticket-schedules/toggle/${schedule.id}`)
      } catch (error) {
        schedule.is_enabled = original
        console.error('Error toggling schedule:', error)
        this.toast.error('Failed to update schedule status.')
      }
    },
    prettyScheduleType(schedule) {
      return schedule.schedule_type
        ? schedule.schedule_type.charAt(0).toUpperCase() + schedule.schedule_type.slice(1)
        : '—'
    },
    recurrenceSummary(schedule) {
      if (!schedule?.schedule_type) return '—'

      const interval = Math.max(1, Number(schedule.interval_value) || 1)

      if (schedule.schedule_type === 'daily') {
        return interval === 1 ? 'Every day' : `Every ${interval} days`
      }

      if (schedule.schedule_type === 'weekly') {
        const day = this.weekdayName(schedule.day_of_week)
        return interval === 1
          ? `Every ${day}`
          : `Every ${interval} weeks on ${day}`
      }

      if (schedule.schedule_type === 'monthly') {
        const dayLabel = Number(schedule.is_last_day_of_month) === 1
          ? 'last day'
          : `day ${schedule.day_of_month || '—'}`
        return interval === 1
          ? (Number(schedule.is_last_day_of_month) === 1
              ? 'Last day of each month'
              : `Day ${schedule.day_of_month || '—'} of each month`)
          : `Every ${interval} months on ${dayLabel}`
      }

      if (schedule.schedule_type === 'quarterly') {
        const month = this.monthName(schedule.month_of_year)
        const dayLabel = Number(schedule.is_last_day_of_month) === 1
          ? 'last day'
          : `day ${schedule.day_of_month || '—'}`
        return `Every 3 months from ${month} (${dayLabel})`
      }

      if (schedule.schedule_type === 'yearly') {
        const month = this.monthName(schedule.month_of_year)
        const base = `${month} ${schedule.day_of_month || ''}`.trim()
        return interval === 1 ? base : `Every ${interval} years on ${base}`
      }

      return '—'
    },
    weekdayName(value) {
      const map = {
        1: 'Monday',
        2: 'Tuesday',
        3: 'Wednesday',
        4: 'Thursday',
        5: 'Friday',
        6: 'Saturday',
        7: 'Sunday'
      }
      return map[Number(value)] || '—'
    },
    monthName(value) {
      const months = [
        '', 'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      return months[Number(value)] || '—'
    },
    formatDate(value) {
      if (!value) return '—'

      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number)
        const date = new Date(year, month - 1, day)
        return date.toLocaleDateString()
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      return date.toLocaleDateString()
    },
    formatTime(value) {
      if (!value) return '—'

      const parts = String(value).split(':')
      let hour = Number(parts[0] || 0)
      const minute = String(parts[1] || '00').padStart(2, '0')
      const suffix = hour >= 12 ? 'PM' : 'AM'

      hour = hour % 12
      if (hour === 0) hour = 12

      return `${hour}:${minute} ${suffix}`
    }
  }
}
</script>